/**
 * Guided installation, at first launch (SPEC §13).
 *
 * An application opened from a browser tab is an application nobody opens. The
 * wording differs by device because the gesture differs: a tablet gets an icon
 * on the home screen, a phone gets it in the drawer — and Chrome only lets us
 * ask once, from a user gesture.
 *
 * **Sur Android, ce n'est plus ce raccourci qu'on propose.** Chrome fabrique
 * bien une application séparée — Family Link la voit, on peut lui accorder un
 * temps illimité — mais c'est *Chrome* qui la dessine : couper Chrome coupe le
 * moteur, et le temps accordé ne porte alors sur rien. Constaté sur les deux
 * appareils le 1ᵉʳ septembre 2026. La coquille `net.harzallah.tuteur` rend la
 * même page avec la WebView du système, que le contrôle parental ne supervise
 * pas ; c'est elle qu'on offre ici, en téléchargement direct.
 *
 * Ailleurs qu'Android — un ordinateur, un iPad — rien de tout cela ne s'applique
 * et l'invite d'origine reste la bonne.
 *
 * Dans la coquille elle-même, la bannière ne peut pas apparaître : une WebView
 * n'émet pas `beforeinstallprompt`. Il n'y a donc rien à détecter.
 */

import { useEffect, useState } from "react";

type InstallEvent = Event & { prompt: () => Promise<void>; userChoice: Promise<unknown> };

/** Le chemin est servi par l'API, qui redirige vers le seau (`routers.telechargement`). */
export const LIEN_APK = "/tuteur.apk";

function surAndroid(): boolean {
  return /Android/i.test(navigator.userAgent);
}

export function InstallPrompt({ device }: { device: "tablet" | "phone" }) {
  const [event, setEvent] = useState<InstallEvent | null>(null);
  const [dismissed, setDismissed] = useState(() => localStorage.getItem("tuteur.install") === "no");

  useEffect(() => {
    const capture = (raw: Event) => {
      raw.preventDefault();
      setEvent(raw as InstallEvent);
    };
    window.addEventListener("beforeinstallprompt", capture);
    return () => window.removeEventListener("beforeinstallprompt", capture);
  }, []);

  if (!event || dismissed) return null;

  const plusTard = (
    <button
      type="button"
      className="btn btn--quiet"
      onClick={() => {
        localStorage.setItem("tuteur.install", "no");
        setDismissed(true);
      }}
    >
      Plus tard
    </button>
  );

  if (surAndroid()) {
    return (
      <div className="banner" data-tone="waiting" role="status">
        Installe l'application Tuteur : elle s'ouvre sans passer par Chrome, donc elle reste
        disponible même quand le temps de Chrome est écoulé.{" "}
        <a className="btn btn--secondary" href={LIEN_APK} download>
          Télécharger
        </a>{" "}
        {plusTard}
      </div>
    );
  }

  return (
    <div className="banner" data-tone="waiting" role="status">
      {device === "tablet"
        ? "Ajoute le tuteur sur ton écran d'accueil : c'est plus rapide à ouvrir."
        : "Installe le tuteur sur ton téléphone : ouverture directe, sans navigateur."}{" "}
      <button
        type="button"
        className="btn btn--secondary"
        onClick={() => {
          void event.prompt();
          setEvent(null);
        }}
      >
        Installer
      </button>{" "}
      {plusTard}
    </div>
  );
}
